/**
 * @fileoverview Global Authentication Context
 *
 * Provides a single source of truth for authentication state across the app.
 * This prevents the "flash of empty state" when navigating between pages,
 * which was caused by each page re-initializing auth state independently.
 *
 * @module contexts/AuthContext
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { logEventDirect } from "@/hooks/useEventLogger";
import { setErrorTrackingUser } from "@/services/errorTracking";
import { Capacitor } from "@capacitor/core";
import {
  signInWithGoogleNative,
  signInWithAppleNative,
} from "@/services/nativeSocialAuth";

import AuthStorage from "@/plugins/AuthStoragePlugin";
import type { SessionData } from "@/plugins/AuthStoragePluginDefinitions";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Sync Supabase session to native iOS storage for share extension access
 */
async function syncSessionToNative(session: Session | null): Promise<void> {
  console.log("🔵 [AuthProvider] syncSessionToNative called", {
    hasSession: !!session,
    isNative: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
  });

  if (!Capacitor.isNativePlatform()) {
    console.log("⏭️ [AuthProvider] Not on native platform, skipping sync");
    return;
  }

  try {
    if (session) {
      console.log("📤 [AuthProvider] Syncing session TO native storage...");

      // Validate required session fields
      if (!session.refresh_token) {
        console.warn(
          "⚠️ [AuthProvider] Session missing refresh_token, skipping native sync",
        );
        return;
      }

      // Validate environment variables
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      if (!supabaseUrl || !supabaseKey) {
        console.error(
          "❌ [AuthProvider] Missing Supabase environment variables, cannot sync to native",
        );
        console.error(
          "VITE_SUPABASE_URL:",
          supabaseUrl ? "present" : "missing",
        );
        console.error(
          "VITE_SUPABASE_PUBLISHABLE_KEY:",
          supabaseKey ? "present" : "missing",
        );
        return;
      }

      const sessionData: SessionData = {
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        userId: session.user.id,
        expiresAt: session.expires_at || Math.floor(Date.now() / 1000) + 3600,
        supabaseUrl,
        supabaseKey,
      };

      console.log("📦 [AuthProvider] Session data prepared:", {
        userId: sessionData.userId,
        tokenLength: sessionData.accessToken.length,
        expiresAt: new Date(sessionData.expiresAt * 1000).toISOString(),
        hasRefreshToken: !!sessionData.refreshToken,
      });

      console.log("🚀 [AuthProvider] Calling AuthStorage.saveSession()...");
      await AuthStorage.saveSession({ session: sessionData });
      console.log(
        "✅ [AuthProvider] Session synced to native storage successfully",
      );
    } else {
      console.log("🗑️ [AuthProvider] Clearing session from native storage...");
      await AuthStorage.clearSession();
      console.log("✅ [AuthProvider] Session cleared from native storage");
    }
  } catch (error) {
    console.error("❌ [AuthProvider] Failed to sync session to native:", error);
    if (error instanceof Error) {
      console.error("❌ [AuthProvider] Error message:", error.message);
      console.error("❌ [AuthProvider] Error stack:", error.stack);
    }
  }
}

/**
 * Global Auth Provider
 *
 * Initializes auth state ONCE at app startup and maintains it globally.
 * All components use useAuth() to access this shared state, preventing
 * the per-component auth rehydration that caused empty state flashes.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Force a log on component mount to verify logging works
  console.log(
    "🚀🚀🚀 [AuthProvider] COMPONENT MOUNTED - LOGS ARE WORKING 🚀🚀🚀",
  );

  useEffect(() => {
    if (import.meta.env.DEV)
      console.log("[AuthProvider] Mounting - starting auth initialization");
    let isInitialLoad = true;

    // WHY: Get session on mount to restore logged-in state
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (import.meta.env.DEV)
          console.log("[AuthProvider] getSession completed:", {
            hasSession: !!session,
            hasUser: !!session?.user,
            timestamp: Date.now(),
          });
        setUser(session?.user ?? null);
        setErrorTrackingUser(
          session?.user
            ? { id: session.user.id, email: session.user.email ?? undefined }
            : null,
        );
        setLoading(false);
        isInitialLoad = false;
        // Sync to native on initial load
        await syncSessionToNative(session);
      })
      .catch((error) => {
        console.error("[AuthProvider] Failed to get session:", error);
        setUser(null);
        setLoading(false);
        isInitialLoad = false;
      });

    // WHY: Subscribe to auth changes for login/logout/token refresh
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        if (import.meta.env.DEV)
          console.log("[AuthProvider] onAuthStateChange fired:", {
            event: event,
            hasSession: !!session,
            hasUser: !!session?.user,
            isInitialLoad,
            timestamp: Date.now(),
          });
        setUser(session?.user ?? null);
        setErrorTrackingUser(
          session?.user
            ? { id: session.user.id, email: session.user.email ?? undefined }
            : null,
        );

        // Only set loading to false if this is not the initial load
        // (getSession will handle that)
        if (!isInitialLoad) {
          setLoading(false);
        }

        // Sync to native storage for share extension access
        await syncSessionToNative(session);

        // Log auth events
        if (event === "SIGNED_IN" && session?.user) {
          // Persist last-used provider so the AuthDialog can hint on next sign-in
          try {
            const provider = session.user.app_metadata?.provider || "email";
            localStorage.setItem(
              "want_last_auth_provider",
              JSON.stringify({
                provider,
                email: session.user.email || "",
              }),
            );
          } catch {
            /* localStorage unavailable — non-fatal */
          }

          logEventDirect(
            "user_signed_in",
            "auth",
            {
              provider: session.user.app_metadata?.provider || "email",
            },
            session.user.id,
          );
        } else if (event === "SIGNED_OUT") {
          logEventDirect("user_signed_out", "auth", {});
        } else if (event === "USER_UPDATED") {
          logEventDirect("user_updated", "auth", {}, session?.user?.id);
        }
      } catch (error) {
        console.error("[AuthProvider] Error in onAuthStateChange:", error);
        // Don't crash the app - auth state will recover on next change
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Sign in with email and password.
   * @throws {Error} If credentials are invalid
   */
  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }, []);

  /**
   * Create a new account with email and password.
   * @throws {Error} If email is already registered or password too weak
   */
  const signUp = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;

    // Send welcome email via edge function (fire-and-forget)
    if (data.user) {
      supabase.functions
        .invoke("send-welcome-email", {
          body: { user_id: data.user.id },
        })
        .catch((err) => console.error("Welcome email failed:", err));
    }
  }, []);

  /**
   * Sign out the current user and clear cached data.
   * @throws {Error} If sign out fails
   */
  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Clear error tracking user context
    setErrorTrackingUser(null);

    // Clear native storage for share extension (don't let this block signout)
    try {
      await syncSessionToNative(null);
    } catch (error) {
      console.error(
        "[AuthProvider] Failed to clear native session (non-fatal):",
        error,
      );
    }

    // Clear all React Query caches to prevent stale data from previous user
    queryClient.clear();

    // Clear localStorage unlock flag
    localStorage.removeItem("seyu_has_saved");
  }, [queryClient]);

  /**
   * Initiate Google OAuth flow.
   * Redirects to Google, then back to app root on success.
   * @throws {Error} If OAuth initialization fails
   */
  const signInWithGoogle = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      // Native: uses Google Sign-In SDK directly - no browser, no web redirects
      await signInWithGoogleNative();
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: `${window.location.origin}/` },
      });
      if (error) throw error;
    }
  }, []);

  const signInWithApple = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      // Native: uses ASAuthorizationController directly - no browser, no web redirects
      await signInWithAppleNative();
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: { redirectTo: `${window.location.origin}/` },
      });
      if (error) throw error;
    }
  }, []);

  /**
   * Send password reset email.
   * @param email - Email address to send reset link to
   * @throws {Error} If email sending fails
   */
  const resetPassword = useCallback(async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    if (error) throw error;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signUp,
        signOut,
        signInWithGoogle,
        signInWithApple,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access global auth context.
 * Must be used within AuthProvider.
 */
export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
